import styled from '@emotion/styled';
import { useState } from 'react';
import { MenuButton, MenuContainer } from '~/modules/Elements/Menu';
import Modal from '~/modules/Elements/Modal';
import SimplifiedMic from '~/modules/GameEngine/Input/SimplifiedMic';
import RemoteMicClient from '~/modules/RemoteMic/Network/Client';
import { transportErrorReason } from '~/modules/RemoteMic/Network/Client/NetworkClient';
import RemoteInputLag from '~/routes/RemoteMic/Panels/RemoteSettings/InputLag';
import ManagePlayers from '~/routes/RemoteMic/Panels/RemoteSettings/ManagePlayers';
import MicrophoneSettings from '~/routes/RemoteMic/Panels/RemoteSettings/MicrophoneSettings';
import { ConnectionStatuses } from '~/routes/RemoteMic/RemoteMic';
import usePermissions from '~/routes/RemoteMic/hooks/usePermissions';
import UnassignAfterSongFinishedSetting from './RemoteSettings/UnassignAfterSongFinishedSetting';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  isKeepAwakeOn: boolean;
  setIsKeepAwakeOn: (keepAwake: boolean) => void;
  monitoringStarted: boolean;
  setMonitoringStarted: (micMonitoring: boolean) => void;
}

function RemoteSettings({ setIsKeepAwakeOn, monitoringStarted, isKeepAwakeOn }: Props) {
  const permissions = usePermissions();
  const [openedPanel, setOpenedPanel] = useState<'microphone' | 'manage' | null>(
    permissions === 'write' ? null : 'microphone',
  );

  return (
    <Container>
      <HeaderRow>
        <span>Remote mic ID:</span>
        <strong data-test="remote-mic-id">{RemoteMicClient.getClientId()?.slice(-4).toUpperCase() ?? '----'}</strong>
      </HeaderRow>

      <Divider />

      {permissions === 'write' && openedPanel === null && (
        <Buttons>
          <SettingsButton
            onClick={() => setOpenedPanel('microphone')}
            size="small"
            data-test="microphone-settings"
            disabled={openedPanel === 'microphone'}>
            Microphone settings
          </SettingsButton>

          <SettingsButton
            onClick={() => setOpenedPanel('manage')}
            size="small"
            data-test="manage-game"
            disabled={openedPanel === 'manage'}>
            Manage the game
          </SettingsButton>
        </Buttons>
      )}

      {permissions !== 'write' && <MicrophoneSettings />}

      <Modal onClose={() => setOpenedPanel(null)} open={openedPanel !== null && permissions === 'write'}>
        <MenuContainer>
          <ModalHeader>{openedPanel === 'microphone' ? 'Microphone settings' : 'Manage game'}</ModalHeader>

          {openedPanel === 'microphone' && <MicrophoneSettings />}

          {openedPanel === 'manage' && (
            <>
              <ManagePlayers />
              <RemoteInputLag />
              <UnassignAfterSongFinishedSetting />
            </>
          )}

          <Divider />

          <SettingsButton onClick={() => setOpenedPanel(null)} size="small" data-test="close-modal">
            Close
          </SettingsButton>
        </MenuContainer>
      </Modal>

      <Footer>
        <FooterToggle role="button" className="typography" onClick={() => setIsKeepAwakeOn(!isKeepAwakeOn)}>
          WakeLock: <strong>{isKeepAwakeOn ? 'ON' : 'OFF'}</strong>
        </FooterToggle>

        <FooterToggle
          role="button"
          className="typography"
          onClick={() => (monitoringStarted ? SimplifiedMic.stopMonitoring() : SimplifiedMic.startMonitoring())}>
          Microphone: <strong data-test="monitoring-state">{monitoringStarted ? 'ON' : 'OFF'}</strong>
        </FooterToggle>
      </Footer>
    </Container>
  );
}

export default RemoteSettings;

const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  padding: 16px;
  gap: 12px;
  color: var(--walky-ink);
  font-size: 14px;
  min-height: 0;
`;

const HeaderRow = styled.h3`
  margin: 0;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;

  color: var(--walky-ink);
  font-size: 15px;
  font-weight: 900;

  span {
    color: var(--walky-muted);
    font-size: 12px;
    text-transform: uppercase;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SettingsButton = styled(MenuButton)`
  min-height: 50px !important;
  border-radius: 10px !important;
  font-size: 14px !important;
  font-weight: 900 !important;
`;

const Divider = styled.hr`
  width: 100%;
  margin: 2px 0;
  border: 0;
  border-top: 1px solid var(--walky-border);
`;

const ModalHeader = styled.h1`
  font-size: clamp(1.2rem, 3vw, 1.6rem) !important;
  margin: 0.25rem 0 0.75rem 0;
`;

const Footer = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FooterToggle = styled.div`
  cursor: pointer;
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 12px;
  border: 1px solid var(--walky-border);
  border-radius: 10px;
  background: var(--walky-surface-raised);
  color: var(--walky-ink);
  font-size: 13px;
  font-weight: 800;
`;
